/* eslint-disable max-lines */
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import {
  CreditRedeemableScope,
  REDEEMABLE_SCOPE_LABELS
} from "../../interfaces/wallet/Wallet.types";
import { revokeCredits } from "../../services/Wallet.service";
import { resolveError } from "../../utils/apiErrorMessage";
import InfoTip from "../reservation_v2/cancellation/InfoTip";
import Button from "../ui/Button";
import IconBtn from "../ui/IconBtn";
import Modal from "../ui/Modal";

// Mirrors CreditLotCalculator.PlanRevocation: the scope filters the lots, expiry orders them.
const REVOKE_ORDER_TIP =
  `«${REDEEMABLE_SCOPE_LABELS[CreditRedeemableScope.Any]}» merr nga të gjitha kreditet, ` +
  `«${REDEEMABLE_SCOPE_LABELS[CreditRedeemableScope.EsimOnly]}» dhe ` +
  `«${REDEEMABLE_SCOPE_LABELS[CreditRedeemableScope.TravelOnly]}» vetëm nga ato. ` +
  "Hiqen së pari kreditet që skadojnë më parë; " +
  "ato pa afat, si kreditet nga rimbursimet, hiqen të fundit.";

interface Props {
  email: string;
  onSuccess?: () => void;
}

// Revoking is also how a customer is paid money against a balance: finance pays from the reason.
const RevokeCreditsModal: FC<Props> = ({ email, onSuccess }) => {
  const [show, setShow] = useState(false);
  const [amount, setAmount] = useState("");
  const [scope, setScope] = useState<CreditRedeemableScope>(
    CreditRedeemableScope.Any
  );
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setShow(false);
    setAmount("");
    setScope(CreditRedeemableScope.Any);
    setReason("");
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    const parsed = Number(amount);

    if (!amount.trim() || Number.isNaN(parsed) || parsed <= 0) {
      toast.error("Shuma duhet të jetë më e madhe se zero.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Arsyeja është e detyrueshme.");
      return;
    }

    setSubmitting(true);
    try {
      await revokeCredits({
        email,
        amount: parsed,
        redeemableScope: scope,
        reason: reason.trim()
      });
      toast.success(`U hoqën €${parsed.toFixed(2)} kredite.`);
      handleClose();
      onSuccess?.();
    } catch (err) {
      toast.error(resolveError(err, "Kreditet nuk u hoqën."));
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShow(true)}
        className="text-sm text-red underline"
      >
        Hiq kredite
      </button>
      <Modal
        position="center"
        show={show}
        onClose={handleClose}
        hasHeader={false}
        calcHeight={false}
        modalWidth="440px"
        modalHeight="520px"
        buttonElement={
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center"
          >
            {submitting && (
              <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />
            )}
            Hiq
          </Button>
        }
      >
        <div className="flex justify-center mt-4 relative">
          <h3 className="text-lg font-semibold">Hiq kredite</h3>
          <IconBtn className="absolute right-2 -top-2" onClick={handleClose}>
            <XMarkIcon className="w-4 h-4 text-neutral" strokeWidth={2} />
          </IconBtn>
        </div>

        <p className="px-6 mt-2 text-sm text-gray-600">{email}</p>

        <div className="px-6 mt-3 space-y-3">
          <div>
            <label
              htmlFor="wr-amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Shuma (€)
            </label>
            <input
              id="wr-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <label
                htmlFor="wr-scope"
                className="block text-sm font-medium text-gray-700"
              >
                Nga cilat kredite të hiqet
              </label>
              <InfoTip text={REVOKE_ORDER_TIP} />
            </div>
            <select
              id="wr-scope"
              value={scope}
              onChange={(e) => setScope(Number(e.target.value))}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {Object.entries(REDEEMABLE_SCOPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="wr-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Arsyeja <span className="text-red">*</span>
            </label>
            <textarea
              id="wr-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RevokeCreditsModal;
